import React, { useEffect, useMemo, useState } from "react";
import { Box } from "theme-ui";
import {
  Button as LumosButton,
  ChoiceInput,
  Form as LumosForm,
  Heading,
  Modal,
  ModalControls,
  TextInput,
} from "@loanpal/lumos__core";
import { array, object, string } from "yup";
import { email as validateEmail } from "../../utils/formValidation";
import {
  ADMIN_ERROR_DEFAULT_ADMIN_EMAIL,
  ADMIN_ERROR_FROM_EMAIL,
  buildAdminErrorEmailPreview,
} from "../../utils/admin-error-email-templates";
import { listAdminErrorEmailSends } from "../../utils/api";
import { dateTimeStr } from "../../utils/format";

function defaultSelectedEmails(log) {
  const partner = String(log?.partnerPrimaryContactEmail ?? "").trim();
  if (partner && validateEmail(partner)) return [partner];
  return [ADMIN_ERROR_DEFAULT_ADMIN_EMAIL];
}

function buildRecipientItems(log) {
  const items = [];
  const partner = String(log?.partnerPrimaryContactEmail ?? "").trim();
  const owner = String(log?.salesforceAccountOwnerEmail ?? "").trim();
  const requester = String(log?.requesterEmail ?? "").trim();

  if (partner) {
    items.push({ label: `Partner: ${partner}`, value: partner });
  }
  if (owner && owner.toLowerCase() !== partner.toLowerCase()) {
    items.push({
      label: `Salesforce account owner: ${owner}`,
      value: owner,
    });
  }
  if (requester) {
    const seen = new Set(items.map((item) => item.value.toLowerCase()));
    if (!seen.has(requester.toLowerCase())) {
      items.push({ label: `Requester: ${requester}`, value: requester });
    }
  }
  items.push({
    label: `Admin: ${ADMIN_ERROR_DEFAULT_ADMIN_EMAIL}`,
    value: ADMIN_ERROR_DEFAULT_ADMIN_EMAIL,
  });
  items.push({ label: "Other", value: "other" });
  return items;
}

function formatSendSource(source, mode) {
  if (source === "manual") return "Manual";
  if (source === "auto") {
    if (mode === "test") return "Auto (test)";
    if (mode === "partner") return "Auto (partner)";
    if (mode) return `Auto (${mode})`;
    return "Auto";
  }
  return source || mode || "—";
}

const SendAdminErrorEmailModal = ({
  log,
  isOpen,
  sending,
  onDismiss,
  onSend,
}) => {
  const preview = useMemo(
    () => (log ? buildAdminErrorEmailPreview(log) : null),
    [log]
  );
  const recipientItems = useMemo(
    () => (log ? buildRecipientItems(log) : []),
    [log]
  );
  const initialEmails = useMemo(
    () => (log ? defaultSelectedEmails(log) : []),
    [log]
  );
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !log?.id) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    listAdminErrorEmailSends(log.id)
      .then((items) => {
        if (!cancelled) setHistory(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        console.log("Failed to load email send history", err);
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, log?.id]);

  if (!log) return null;

  return (
    <Modal
      variant="full"
      isOpen={isOpen}
      onDismiss={onDismiss}
      title="Send error email (test)"
    >
      <Box className="admin-error-log-send-email-modal" sx={styles.modalContent}>
        <Heading
          sx={{
            backgroundColor: "#ffffff",
            fontSize: "18px",
            whiteSpace: "pre-line",
            marginBottom: "12px",
            flexShrink: 0,
          }}
        >
          {log.accountName || log.clientId || "Error log"}
        </Heading>

        <Box sx={styles.historyBox}>
          <Heading sx={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
            Send history
          </Heading>
          {historyLoading ? (
            <Box sx={{ fontSize: "13px", color: "#666" }}>Loading…</Box>
          ) : history.length === 0 ? (
            <Box sx={{ fontSize: "13px", color: "#666" }}>
              No sends recorded for this error yet.
            </Box>
          ) : (
            <table className="admin-error-email-history-table">
              <thead>
                <tr>
                  <th>Sent</th>
                  <th>Source</th>
                  <th>To</th>
                  <th>CC</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id}>
                    <td>{dateTimeStr(row.sentAt || row.createdAt)}</td>
                    <td>{formatSendSource(row.source, row.mode)}</td>
                    <td title={row.toEmails || ""}>{row.toEmails || "—"}</td>
                    <td title={row.ccEmails || ""}>{row.ccEmails || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Box>

        <Box sx={styles.sendForm}>
          <LumosForm
            key={log.id}
            initialValues={{
              emails: initialEmails,
              otherEmail: "",
            }}
            validationSchema={object({
              emails: array().of(string()).min(1, "Select at least one email"),
              otherEmail: string(),
            })}
            onSubmit={(values, { setFieldError }) => {
              if (
                values.emails.includes("other") &&
                !String(values.otherEmail ?? "").trim()
              ) {
                setFieldError("otherEmail", "Please enter a valid email");
                return;
              }
              if (
                values.otherEmail &&
                !validateEmail(String(values.otherEmail).trim())
              ) {
                setFieldError("otherEmail", "Please enter a valid email");
                return;
              }

              const selected = [
                ...values.emails.filter((entry) => entry !== "other"),
                ...(values.otherEmail ? [String(values.otherEmail).trim()] : []),
              ].filter(Boolean);

              const unique = selected.filter(
                (address, index) =>
                  selected.findIndex(
                    (item) => item.toLowerCase() === address.toLowerCase()
                  ) === index
              );

              if (!unique.length) {
                setFieldError("emails", "Select at least one email");
                return;
              }

              onSend(unique);
            }}
          >
            <ChoiceInput
              name="emails"
              allowMultiple
              label="Send email to"
              description="Select email(s) to send this error notification to"
              items={recipientItems}
            />
            <TextInput name="otherEmail" placeholder="j.doe@example.com" />
            <Box sx={{ marginTop: "16px", marginBottom: "8px" }}>
              <LumosButton type="submit" disabled={sending}>
                {sending ? "Sending…" : "Send Email"}
              </LumosButton>
            </Box>
          </LumosForm>
        </Box>

        <Heading sx={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px", flexShrink: 0 }}>
          Email preview
        </Heading>
        <Box sx={styles.previewMeta}>
          <div>
            <span style={styles.previewLabel}>From:</span> {ADMIN_ERROR_FROM_EMAIL}
          </div>
          <div>
            <span style={styles.previewLabel}>Subject:</span> {preview?.subject}
          </div>
          {preview?.fixSubmittingUserUrl ? (
            <div style={styles.fixLinkRow}>
              <span style={styles.previewLabel}>Fix link:</span>{" "}
              <a
                href={preview.fixSubmittingUserUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.fixLink}
              >
                {preview.fixSubmittingUserUrl}
              </a>
            </div>
          ) : null}
        </Box>
        <Box sx={styles.previewBody}>
          {preview?.htmlBody ? (
            <iframe
              title="Email preview"
              srcDoc={preview.htmlBody}
              style={styles.previewIframe}
              scrolling="yes"
            />
          ) : (
            <pre style={styles.previewPre}>{preview?.body}</pre>
          )}
        </Box>
      </Box>

      <ModalControls
        actions={[
          {
            onClick: onDismiss,
            label: "Close",
          },
        ]}
      />
    </Modal>
  );
};

const styles = {
  modalContent: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 140px)",
    overflowY: "auto",
    overflowX: "hidden",
    paddingBottom: "8px",
  },
  previewMeta: {
    backgroundColor: "#f5f5f5",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "12px 16px",
    marginBottom: "12px",
    fontSize: "14px",
    lineHeight: 1.5,
    flexShrink: 0,
  },
  previewLabel: {
    color: "#666",
    display: "inline-block",
    minWidth: "64px",
  },
  previewBody: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: 0,
    flex: "none",
    height: "min(52vh, 520px)",
    minHeight: "280px",
    overflow: "hidden",
    marginBottom: "8px",
  },
  previewHtml: {
    padding: "8px 12px",
    backgroundColor: "#fff",
    color: "#323232",
  },
  previewIframe: {
    border: "none",
    width: "100%",
    height: "100%",
    display: "block",
    backgroundColor: "#fff",
  },
  sendForm: {
    flexShrink: 0,
    marginBottom: "16px",
    paddingBottom: "8px",
    borderBottom: "1px solid #eee",
  },
  historyBox: {
    flexShrink: 0,
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #eee",
  },
  previewPre: {
    margin: 0,
    padding: "12px 16px",
    height: "100%",
    overflowY: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  fixLinkRow: {
    marginTop: "12px",
    paddingTop: "12px",
    borderTop: "1px solid #eee",
  },
  fixLink: {
    color: "#0066cc",
    fontWeight: 600,
    textDecoration: "underline",
    wordBreak: "break-all",
  },
};

export default SendAdminErrorEmailModal;
